import React, { useState, useEffect } from 'react';
import { Users, UserPlus, MessageCircle } from 'lucide-react';

export default function FriendsList({ user, onViewAll, onMessage }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Load real friends data from API
  useEffect(() => {
    if (user) {
      loadFriends();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadFriends = async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const response = await fetch(`${API_URL}/api/friends/${user.uid}`, { signal: controller.signal });
      clearTimeout(timeout);
      if (!response.ok) { setLoading(false); return; }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) { setLoading(false); return; }
      const data = await response.json();

      if (data.success) {
        setFriends(data.friends || []);
      }
      setLoading(false);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error loading friends:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-6">
        <Users className="w-10 h-10 mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-500">로그인하여 친구를 확인하세요</p>
      </div>
    );
  }

  if (friends.length === 0) {
    return (
      <div className="text-center py-6">
        <Users className="w-10 h-10 mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-500 mb-3">아직 친구가 없습니다</p>
        <button
          onClick={onViewAll}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          친구 찾기
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
        {friends.map((friend) => (
          <div
            key={friend.id}
            className="flex items-center justify-between hover:bg-gray-50 p-2 rounded-lg transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="relative">
                <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                  <span className="text-xl">{friend.avatar}</span>
                </div>
                {friend.status === 'online' && (
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm text-gray-900 truncate">
                  {friend.name}
                </h4>
                <p className="text-xs text-gray-500 truncate">
                  {friend.username}
                </p>
              </div>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onMessage) {
                  console.log('Opening chat with friend:', friend);
                  onMessage(friend.id); // Changed from friend.userId to friend.id
                }
              }}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title="메시지 보내기"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={onViewAll}
        className="w-full text-center py-2 text-sm text-gray-600 hover:text-blue-600 font-medium hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        <UserPlus className="w-4 h-4" />
        친구 추가
      </button>
    </div>
  );
}
